import { Component, ElementRef, Inject, Input, OnDestroy } from '@angular/core';
import { DataService } from '@app/core/services/data.service';
import { JointApplicantService } from './joint-applicant.service';
import { ApplicationFormDataService } from '../application-form/application-form-data.service';
import { ApplicationFormService } from '../application-form/application-form.service';
import { BehaviorSubject, Observable, Subject, firstValueFrom, of, takeUntil } from 'rxjs';
import { LoggerService } from '@app/blocks/logger/services/logger.service';
import { AbstractControl, FormGroup } from '@angular/forms';
import { FormlyFieldConfig, FormlyModule } from '@ngx-formly/core';
import { regEx } from '@app/core/constants/regEx';
import { formatFormErrors } from '@app/shared/formly/form-config';
import { SlideCommonService } from '../common/slide-common.service';
import { AsyncPipe, CommonModule, NgFor, NgIf } from '@angular/common';
import { ConnectSlideComponent } from '@app/blocks/ui/slide/slide.component';
import { SpinnerComponent } from '@app/shared/components/spinner/spinner.component';
import { WINDOW, WINDOW_PROVIDERS } from '@app/shared/services/window/window.service';

@Component({
    selector: 'app-joint-applicant',
    templateUrl: './joint-applicant.component.html',
    providers: [
        JointApplicantService,
        WINDOW_PROVIDERS
    ],
    imports: [
        CommonModule,
        ConnectSlideComponent,
        SpinnerComponent,
        FormlyModule,
        AsyncPipe,
        NgIf,
        NgFor
    ]
})
export class JointApplicantComponent implements OnDestroy {
    @Input() slideNumber: string | undefined;
    @Input() finalSlide = false;
    currentSlide = 'joint';
    jointApplication = this.afs.getJointApplication();
    active = this.afs.getActive();
    showSubmit = false;
    showError = false;
    showFormErrors = false; //error summary
    errorMessage = "";
    errors: any = [];
    isDisabled = false;
    submitting = false;

    form: FormGroup = new FormGroup(
        {}
    );
    fields: BehaviorSubject<any> = new BehaviorSubject([]);
    model = this.afs.getJointModel();

    private destroy$ = new Subject<void>();

    subscription = this.afs.subscribeJointModel((data: any) => {
        this.model = data;

        const observables: Observable<any>[] = [
            of(this.jointApplicantService.fetchForm(this.globalValidators, this.changeFns)),
            // of(this.afs.getFormState()),
        ]

        this.dataService.ready(observables).pipe(takeUntil(this.destroy$)).subscribe(
            {
                next: ([ form ]) => {
                    this.fields.next(form);

                    // set the default value before on load
                    if (this.model.previousAddresses && this.model.previousAddresses.length === 0) {
                        this.model.previousAddresses = [ null ]
                    }
                    if (this.model.criminalConvictions && this.model.criminalConvictions.length === 0) {
                        this.model.criminalConvictions = [ null ]
                    }

                    this.jointApplicantService.setCachedNiNumber(this.model.niNumber);
                    this.addressSameChange(this.model.isTheAddressTheSame);

                    this.showSubmit = true;
                },
                error: () => {
                    console.log('"ready" function failed');
                }
            }
        );
    });

    constructor(
        private dataService: DataService,
        private applicationFormDataService: ApplicationFormDataService,
        private afs: ApplicationFormService,
        private jointApplicantService: JointApplicantService,
        private slideCommon: SlideCommonService,
        private logger: LoggerService,
        private el: ElementRef,
        @Inject(WINDOW) private window: Window,
    ) {
    }

    get formState() {
        return this.afs.formState;
    }

    /*
      * Unabled to bind to jointApplicant address fields due to conflict with ASF
      * using lead applicant fields instead
      */
    addressSameChange(isTheAddressTheSame: boolean) {
        if (isTheAddressTheSame) {
            const lead = this.afs.getLeadModel();

            this.model.addressLine1 = lead.addressLine1;
            this.model.addressLine2 = lead.addressLine2;
            this.model.addressLine3 = lead.addressLine3;
            this.model.city = lead.city;
            this.model.county = lead.county;
            this.model.country = lead.country;
            this.model.postCode = lead.postCode;
            this.model.durationAtCurrentProperty = lead.durationAtCurrentProperty;

            this.form.get('addressLine1')?.setValue(lead.addressLine1);
            this.form.get('addressLine2')?.setValue(lead.addressLine2);
            this.form.get('addressLine3')?.setValue(lead.addressLine3);
            this.form.get('city')?.setValue(lead.city);
            this.form.get('county')?.setValue(lead.county);
            this.form.get('country')?.setValue(lead.country);
            this.form.get('postCode')?.setValue(lead.postCode);
            this.form.get('durationAtCurrentProperty')?.setValue(lead.durationAtCurrentProperty);
        }
        else {
            this.model.addressLine1 = this.model.jointApplicantAddressLine1;
            this.model.addressLine2 = this.model.jointApplicantAddressLine2;
            this.model.addressLine3 = this.model.jointApplicantAddressLine3;
            this.model.city = this.model.jointApplicantCity;
            this.model.county = this.model.jointApplicantCounty;
            this.model.country = this.model.jointApplicantCountry;
            this.model.postCode = this.model.jointApplicantPostCode;
            this.model.durationAtCurrentProperty = this.model.jointApplicantDurationAtCurrentProperty;

            this.form.get('addressLine1')?.setValue(this.model.jointApplicantAddressLine1);
            this.form.get('addressLine2')?.setValue(this.model.jointApplicantAddressLine2);
            this.form.get('addressLine3')?.setValue(this.model.jointApplicantAddressLine3);
            this.form.get('city')?.setValue(this.model.jointApplicantCity);
            this.form.get('county')?.setValue(this.model.jointApplicantCounty);
            this.form.get('country')?.setValue(this.model.jointApplicantCountry);
            this.form.get('postCode')?.setValue(this.model.jointApplicantPostCode);
            this.form.get('durationAtCurrentProperty')?.setValue(this.model.jointApplicantDurationAtCurrentProperty);
        }

        this.form.updateValueAndValidity();
    }

    globalValidators = {
        emailPatternCheck: this.emailPatternCheck,
        emailSameCheck: this.emailSameCheck,
        jointApplicantLengthOfTimeAtProperty: this.jointApplicantLengthOfTimeAtProperty
    }

    changeFns = {
        addressSameChange: (isTheAddressTheSame: boolean) => this.addressSameChange(isTheAddressTheSame),
    }

    onBack() {
        this.isDisabled = false;
        this.submitting = false;

        this.afs.goBack('joint');

        this.slideCommon.scroll(this.active.active)
    }

    onSubmit() {
        this.logger.info('joint submit clicked', this.model.applicationContactAddressId);
        this.form.markAllAsTouched();

        if (this.form.valid) {
            console.log("VALID");
            this.errors = [];
            this.showFormErrors = false;

            this.isDisabled = true;
            this.submitting = true;

            this.afs.addAddressIds("joint");

            console.log(this.model);

            firstValueFrom(this.applicationFormDataService.postJointApplicant(this.model, this.afs.getLeadModel())).then((response: any) => {
                console.log('joint applicant post success ' + response.status);

                this.slideCommon.save(this.form, this.currentSlide, this.formState, this.active);

                this.isDisabled = false;
                this.submitting = false;

            }).catch((error) => {

                this.isDisabled = false;
                this.submitting = false;
                this.logger.error(error);
            });
        }
        else {
            console.log("INVALID", this.form.errors)
            const renderTime = 500;
            const revealTime = renderTime + 250;

            setTimeout(() => {
                this.errors = formatFormErrors(this.el, this.form);
                this.showFormErrors = true;
            }, renderTime);

            setTimeout(() => {
                this.scrollToId('joint-error-summary');
            }, revealTime);
        }
    }

    scrollToId(id: string) {
        this.slideCommon.scrollToId(id, this.el, this.window);
    }

    emailSameCheck(c: AbstractControl, f: FormlyFieldConfig, lead: any) {
        if (!c.value) {
            //default to required
            return false;
        }

        const jointEmail = c.value.toLowerCase();
        const leadEmail = lead.emailAddress.toLowerCase();

        if (jointEmail === leadEmail) { // set error state
            // logger.info('email address matches');
            return false;
        }
        else {
            return true;
        }
    }

    emailPatternCheck(c: AbstractControl) {
        // this.logger.info('emailCheck', c.value);
        if (!c.value) {
            //default to required
            return false;
        }

        const emailRegEx = regEx.emailPattern;

        const validEmail = emailRegEx.test(c.value);

        if (!validEmail) {
            // this.logger.info('invalid email address');
            return false;
        }
        else {
            return true;
        }
    }

    jointApplicantLengthOfTimeAtProperty(c: AbstractControl, f: FormlyFieldConfig) {
        // // because this code now runs hot as part of a validator (to keep sync with the other form errors), logs are disabled
        // console.log('lengthOfTimeAtPropertyChange');

        let lengthOfTimeAtCurrentProperty = 0;
        let lengthOfTimeAtPreviousProperties = 0;

        if (parseInt(f.model.durationAtCurrentProperty)) {
            lengthOfTimeAtCurrentProperty = parseInt(f.model.durationAtCurrentProperty);
        }

        if (f.model.previousAddresses) {
            for (let j = 0; j < f.model.previousAddresses.length; j++) {
                if (f.model.previousAddresses[ j ] && parseInt(f.model.previousAddresses[ j ].durationAtPreviousProperty)) {
                    lengthOfTimeAtPreviousProperties = lengthOfTimeAtPreviousProperties + parseInt(f.model.previousAddresses[ j ].durationAtPreviousProperty);
                }
            }
        }

        const totalAddressDuration = lengthOfTimeAtCurrentProperty + lengthOfTimeAtPreviousProperties;

        // console.log('totalAddressDuration ' + totalAddressDuration);

        if (totalAddressDuration < 60 && f.model.previousAddresses) {
            // console.log('triggered < 60');
            return false;

        } else if (totalAddressDuration >= 60 && f.model.previousAddresses) {
            // console.log('triggered >= 60');
            return true;
        }
        else if (totalAddressDuration >= 60 && !f.model.previousAddresses) {
            // console.log('triggered >= 60');
            return true;
        }
        return false;
    }

    ngOnDestroy() {
        this.destroy$.next();
        this.destroy$.complete();

        this.subscription.unsubscribe();
    }
}
